import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  IconButton,
  Chip,
  Paper,
  alpha,
  useTheme,
  CircularProgress,
  Snackbar,
  Alert,
  Container
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { supabase } from '../../../../supabaseClient';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { usePermissions } from '../../../../utils/permissions';

const DocumentsSection = ({ documents, setDocuments, conditionOfGoods, setConditionOfGoods, onSave }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { companyId, id: poId } = useParams();
  const { canViewDeliveryDocuments, canEditDeliveryDocuments, canDeleteDeliveryDocuments } = usePermissions();
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, document: null });
  const [documentsCompleted, setDocumentsCompleted] = useState(false);

  // Permission checks
  const canView = canViewDeliveryDocuments();
  const canEdit = canEditDeliveryDocuments();
  const canDelete = canDeleteDeliveryDocuments();

  useEffect(() => {
    if (poId && poId !== 'new') {
      fetchDocuments();
      fetchConditionNotes();
      fetchCompletionStatus();
    }
  }, [poId]);

  const fetchDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('po_confirmation_documents')
        .select('*')
        .eq('purchase_order_id', poId)
        .order('uploaded_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setSnackbar({ open: true, message: 'Failed to fetch documents', severity: 'error' });
    }
  };

  const fetchConditionNotes = async () => {
    try {
      const { data, error } = await supabase
        .from('po_delivery_confirmations')
        .select('initial_condition_notes')
        .eq('purchase_order_id', poId)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // Ignore "not found" error
      if (data?.initial_condition_notes) {
        setConditionOfGoods(data.initial_condition_notes);
      }
    } catch (err) {
      console.error('Error fetching condition notes:', err);
    }
  };

  const fetchCompletionStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('po_delivery_confirmations')
        .select('documents_completed')
        .eq('purchase_order_id', poId)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // Ignore "not found" error
      if (data) {
        setDocumentsCompleted(data.documents_completed || false);
      }
    } catch (err) {
      console.error('Error fetching completion status:', err);
    }
  };

  const saveConditionNotes = async () => {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      // Check if record exists
      const { data: existing } = await supabase
        .from('po_delivery_confirmations')
        .select('id')
        .eq('purchase_order_id', poId)
        .single();

      if (existing) {
        // Update existing record
        const { error } = await supabase
          .from('po_delivery_confirmations')
          .update({ 
            initial_condition_notes: conditionOfGoods,
            updated_at: new Date().toISOString()
          })
          .eq('purchase_order_id', poId);

        if (error) throw error;
      } else {
        // Create new record
        const { error } = await supabase
          .from('po_delivery_confirmations')
          .insert({
            company_id: companyId,
            purchase_order_id: poId,
            initial_condition_notes: conditionOfGoods,
            created_by: user.id
          });

        if (error) throw error;
      }

      setSnackbar({ open: true, message: 'Condition notes saved successfully', severity: 'success' });
      
      // Reload completion flags
      if (onSave) onSave();
    } catch (err) {
      console.error('Error saving condition notes:', err);
      setSnackbar({ open: true, message: 'Failed to save condition notes', severity: 'error' });
    }
  };

  const handleToggleComplete = async () => {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const newCompletedStatus = !documentsCompleted;

      // Check if record exists
      const { data: existing } = await supabase
        .from('po_delivery_confirmations')
        .select('id')
        .eq('purchase_order_id', poId)
        .single();

      if (existing) {
        // Update existing record
        const { error } = await supabase
          .from('po_delivery_confirmations')
          .update({ 
            documents_completed: newCompletedStatus,
            updated_at: new Date().toISOString()
          })
          .eq('purchase_order_id', poId);

        if (error) throw error;
      } else {
        // Create new record
        const { error } = await supabase
          .from('po_delivery_confirmations')
          .insert({
            company_id: companyId,
            purchase_order_id: poId,
            documents_completed: newCompletedStatus,
            created_by: user.id
          });

        if (error) throw error;
      }

      // Update local state
      setDocumentsCompleted(newCompletedStatus);

      setSnackbar({ 
        open: true, 
        message: newCompletedStatus ? 'Documents section marked as complete' : 'Documents section marked as incomplete', 
        severity: 'success' 
      });
      
      // Reload completion flags
      if (onSave) onSave();
    } catch (err) {
      console.error('Error toggling completion status:', err);
      setSnackbar({ open: true, message: 'Failed to update completion status', severity: 'error' });
    }
  };

  const handleFileSelect = async (event, category) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      // Get user profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', user.id)
        .single();

      for (const file of files) {
        // Upload to storage
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${companyId}/${poId}/${category}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('po-confirmation-documents')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        // Save metadata to database
        const { data: docData, error: dbError } = await supabase
          .from('po_confirmation_documents')
          .insert({
            company_id: companyId,
            purchase_order_id: poId,
            document_category: category,
            file_name: file.name,
            file_size: file.size,
            file_type: file.type,
            file_path: filePath,
            uploaded_by: user.id,
            uploaded_by_name: profile?.full_name || user.email,
            uploaded_by_email: profile?.email || user.email
          })
          .select()
          .single();

        if (dbError) throw dbError;

        // Add to local state
        setDocuments(prev => [...prev, docData]);
      }

      setSnackbar({ open: true, message: `${files.length} file(s) uploaded successfully`, severity: 'success' });
    } catch (err) {
      console.error('Error uploading files:', err);
      setSnackbar({ open: true, message: 'Failed to upload files', severity: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleOpenDeleteDialog = (doc) => {
    setDeleteDialog({ open: true, document: doc });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, document: null });
  };

  const handleConfirmDelete = async () => {
    const doc = deleteDialog.document;
    if (!doc) return;

    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('po-confirmation-documents')
        .remove([doc.file_path]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from('po_confirmation_documents')
        .delete()
        .eq('id', doc.id);

      if (dbError) throw dbError;

      // Remove from local state
      setDocuments(documents.filter(d => d.id !== doc.id));
      setSnackbar({ open: true, message: 'Document deleted successfully', severity: 'success' });
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error deleting document:', err);
      setSnackbar({ open: true, message: 'Failed to delete document', severity: 'error' });
      handleCloseDeleteDialog();
    }
  };

  const handleViewDocument = async (doc) => {
    try {
      const { data, error } = await supabase.storage
        .from('po-confirmation-documents')
        .createSignedUrl(doc.file_path, 3600); // 1 hour expiry

      if (error) throw error;

      window.open(data.signedUrl, '_blank');
    } catch (err) {
      console.error('Error viewing document:', err);
      setSnackbar({ open: true, message: 'Failed to view document', severity: 'error' });
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const { data, error } = await supabase.storage
        .from('po-confirmation-documents')
        .createSignedUrl(doc.file_path, 60); // 1 minute expiry

      if (error) throw error;

      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.href = data.signedUrl;
      link.download = doc.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading document:', err);
      setSnackbar({ open: true, message: 'Failed to download document', severity: 'error' });
    }
  };

  const documentCategories = [
    { value: 'signature', label: 'Signed Documents' },
    { value: 'container', label: 'Container Photos' },
    { value: 'product', label: 'Product Photos' },
    { value: 'other', label: 'Other' }
  ];

  const getDocumentsByCategory = (category) => {
    return documents.filter(doc => doc.document_category === category);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Access Denied Card
  if (!canView) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Delivery Documents Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Delivery Documents. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Card sx={{
      mb: 3,
      borderRadius: 3,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      background: documentsCompleted ? alpha(theme.palette.success.main, 0.03) : 'white',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 20px ${alpha(theme.palette.grey[500], 0.15)}`
      }
    }}>
      <CardContent sx={{ p: 4 }}>
        {/* Section Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              p: 1.5,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}>
              <UploadIcon sx={{ fontSize: 28, color: 'primary.main' }} />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
                  Documents & Photos
                </Typography>
                {documentsCompleted && (
                  <Chip
                    icon={<CheckIcon sx={{ fontSize: 16 }} />}
                    label="COMPLETED"
                    size="small"
                    sx={{
                      height: 24,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      color: 'success.main',
                      border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                      fontWeight: 600,
                      fontSize: '0.7rem'
                    }}
                  />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                Upload signed documents and delivery photos
              </Typography>
            </Box>
          </Box>
          
          <Button
            variant={documentsCompleted ? "outlined" : "contained"}
            color={documentsCompleted ? "warning" : "success"}
            onClick={handleToggleComplete}
            disabled={!canEdit}
            startIcon={documentsCompleted ? null : <CheckIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 500,
              px: 3,
              textTransform: 'none',
              opacity: canEdit ? 1 : 0.3,
              pointerEvents: canEdit ? 'auto' : 'none',
              cursor: canEdit ? 'pointer' : 'not-allowed'
            }}
          >
            {documentsCompleted ? 'Mark Incomplete' : 'Mark Complete'}
          </Button>
        </Box>

        {/* Completion Info Alert */}
        {documentsCompleted && (
          <Alert 
            severity="success" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Documents section marked as complete
            </Typography>
          </Alert>
        )}

        {/* Upload Areas by Category */}
        <Grid container spacing={2}>
          {documentCategories.map((category) => {
            const categoryDocs = getDocumentsByCategory(category.value);
            
            return (
              <Grid item xs={12} sm={6} key={category.value}>
                <Box sx={{
                  p: 2,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.4)}`,
                  borderRadius: 2,
                  bgcolor: 'white'
                }}>
                  {/* Category Title */}
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1.5, color: 'text.primary' }}>
                    {category.label}
                  </Typography>

                  {/* Upload Button */}
                  <Button
                    variant="outlined"
                    component="label"
                    size="small"
                    startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
                    disabled={uploading || !canEdit}
                    sx={{
                      mb: categoryDocs.length > 0 ? 1.5 : 0,
                      borderRadius: 1.5,
                      py: 1,
                      px: 2,
                      fontWeight: 500,
                      fontSize: '0.8rem',
                      borderStyle: 'dashed',
                      borderWidth: 1.5,
                      width: '100%',
                      opacity: canEdit ? 1 : 0.3,
                      pointerEvents: canEdit ? 'auto' : 'none',
                      cursor: canEdit ? 'pointer' : 'not-allowed',
                      '&:hover': {
                        borderStyle: 'dashed',
                        borderWidth: 1.5,
                        bgcolor: alpha(theme.palette.primary.main, 0.02)
                      }
                    }}
                  >
                    <input
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx"
                      hidden
                      onChange={(e) => canEdit && handleFileSelect(e, category.value)}
                      disabled={uploading || !canEdit}
                    />
                    {uploading ? 'Uploading...' : 'Upload File'}
                  </Button>

                  {/* Uploaded Files for this Category */}
                  {categoryDocs.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 1, display: 'block', fontSize: '0.7rem' }}>
                        {categoryDocs.length} file{categoryDocs.length !== 1 ? 's' : ''}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {categoryDocs.map((doc) => (
                          <Box key={doc.id} sx={{
                            p: 1,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.grey[50], 0.5),
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.grey[100], 0.8)
                            }
                          }}>
                            {doc.file_type?.startsWith('image/') ? (
                              <ImageIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                            ) : (
                              <FileIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="caption" sx={{
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                                fontSize: '0.75rem'
                              }}>
                                {doc.file_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                {formatFileSize(doc.file_size)}
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => handleViewDocument(doc)}
                              sx={{
                                color: 'info.main',
                                padding: 0.5,
                                '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.1) }
                              }}
                            >
                              <VisibilityIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadDocument(doc)}
                              sx={{
                                color: 'success.main',
                                padding: 0.5,
                                '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.1) }
                              }}
                            >
                              <DownloadIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => canDelete && handleOpenDeleteDialog(doc)}
                              sx={{
                                color: 'error.main',
                                padding: 0.5,
                                opacity: canDelete ? 1 : 0.3,
                                pointerEvents: canDelete ? 'auto' : 'none',
                                cursor: canDelete ? 'pointer' : 'not-allowed',
                                '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) }
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>

        {/* Condition of Goods Note */}
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
              Initial Condition Notes:
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => canEdit && saveConditionNotes()}
              disabled={uploading || !canEdit}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.75rem',
                opacity: canEdit ? 1 : 0.3,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'pointer' : 'not-allowed'
              }}
            >
              Save Notes
            </Button>
          </Box>
          <Paper sx={{
            p: 2,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.grey[50], 0.5),
            opacity: canEdit ? 1 : 0.6
          }}>
            <textarea
              value={conditionOfGoods}
              onChange={(e) => canEdit && setConditionOfGoods(e.target.value)}
              onBlur={() => canEdit && saveConditionNotes()}
              disabled={!canEdit}
              placeholder="Note any visible condition issues before delivery (optional)..."
              style={{
                width: '100%',
                minHeight: '100px',
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontFamily: 'inherit',
                fontSize: '0.875rem',
                resize: 'vertical',
                cursor: canEdit ? 'text' : 'not-allowed'
              }}
            />
          </Paper>
        </Box>

      </CardContent>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleCloseDeleteDialog}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: 400
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 500 }}>
          Delete Document?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{deleteDialog.document?.file_name}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleCloseDeleteDialog}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            sx={{ borderRadius: 2 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default DocumentsSection;